import type {
  QuartileEvent,
  WebediaPlayer
} from '@webedia-services/webedia-player';
import loadScript from 'load-script';

import { ASSETS_HOST } from 'common/configuration/constants';
import { isProd } from 'common/tools/env';
import normalizeTrackerLabel from 'common/tools/string/tracking';
import { getKeys } from 'common/types';

import { COMSCORE_CLIENT_ID } from 'website/configuration/constants';
import type { PlayerCustomData } from 'website/module/player';

declare global {
  interface Window {
    ns_?: Comscore;
  }
}

type SendObject = {
  action: keyof ReducedRequirementsStreamingAnalyticsInstance;
  streamVal: { [label in AssetLabelNames]: AssetTypes[label] };
};

/**
 * This flag is used to determine whether comscore's lib script was loaded on the page
 * true: we start to send hit to comscore
 * false: we save push hit into a temporary stack and will send it later in next hit request
 * @type {Boolean}
 */
let libLoaded = false;
/**
 * comscore lib api
 */
let contentStreamTag: ReducedRequirementsStreamingAnalyticsInstance | undefined;
/**
 * This is a temporary stack to store the hits
 * @type {Array}
 */
let trackerStacks: SendObject[] = [];

/**
 * This object is used to generate the value of streaming config in each circumstance
 * @type {Object}
 */
const configStreaming: {
  [label in AssetLabelNames]: {
    getValue: (
      webediaPlayer: WebediaPlayer,
      customData: PlayerCustomData
    ) => AssetTypes[label];
  };
} = {
  ns_st_ci: {
    getValue: (webediaPlayer, { id }) =>
      webediaPlayer.postroll?.getCurrentPostroll()?.cmedia ?? id ?? '*null'
  },
  c3: {
    getValue: () => window.location.hostname
  },
  c4: {
    getValue: () => window.location.hostname
  },
  c6: {
    getValue: (webediaPlayer, { id_type, file_type }) =>
      (webediaPlayer.postroll?.isPlayingPostrollVideo()
        ? webediaPlayer.postroll?.getCurrentPostroll()?.mediaType
        : `${id_type}-${file_type}`) ?? '*null'
  },
  ns_st_cl: {
    getValue: webediaPlayer =>
      Math.round(webediaPlayer.getDuration() ?? 0) * 1000
  },
  ns_st_pu: {
    getValue: (webediaPlayer, { relatedEntityDistributor }) =>
      (webediaPlayer.postroll?.isPlayingPostrollVideo()
        ? normalizeTrackerLabel(
            webediaPlayer.postroll?.getCurrentPostroll()?.comscore_ns_st_pu
          )
        : normalizeTrackerLabel(relatedEntityDistributor)) ?? '*null'
  },
  ns_st_pr: {
    getValue: (webediaPlayer, { relatedEntityId, relatedEntityTitle }) => {
      if (webediaPlayer.postroll?.isPlayingPostrollVideo()) {
        const currentPostroll = webediaPlayer.postroll?.getCurrentPostroll();
        const ref = currentPostroll?.ref;
        const refTitle = currentPostroll?.refTitle;
        return ref && refTitle
          ? `${ref}-${normalizeTrackerLabel(refTitle)}`
          : '*null';
      }
      return relatedEntityId && relatedEntityTitle
        ? `${relatedEntityId}-${normalizeTrackerLabel(relatedEntityTitle)}`
        : '*null';
    }
  },
  ns_st_ep: {
    getValue: (webediaPlayer, { id, description }) =>
      webediaPlayer.postroll?.isPlayingPostrollVideo()
        ? `${normalizeTrackerLabel(
            webediaPlayer.postroll?.getCurrentPostroll()?.xt_title ?? '*null'
          )}`
        : id && description
          ? `${id}-${normalizeTrackerLabel(description)}`
          : '*null'
  },
  ns_st_sn: {
    getValue: webediaPlayer =>
      webediaPlayer.postroll?.isPlayingPostrollVideo()
        ? normalizeTrackerLabel(
            `${
              webediaPlayer.postroll?.getCurrentPostroll()?.comscore_ns_st_sn
            }` ?? '*null'
          )
        : '*null'
  },
  ns_st_en: {
    getValue: webediaPlayer =>
      webediaPlayer.postroll?.isPlayingPostrollVideo()
        ? normalizeTrackerLabel(
            `${
              webediaPlayer.postroll?.getCurrentPostroll()?.comscore_ns_st_en
            }` ?? '*null'
          )
        : '*null'
  },
  ns_st_st: {
    getValue: () => '*null'
  },
  ns_st_ge: {
    getValue: () => '*null'
  },
  ns_st_ia: {
    getValue: () => 0
  },
  ns_st_ce: {
    getValue: () => 0
  },
  ns_st_ddt: {
    getValue: () => '*null'
  },
  ns_st_tdt: {
    getValue: () => '*null'
  },
  ns_st_tep: {
    getValue: () => undefined
  },
  ns_st_tpr: {
    getValue: () => undefined
  }
};

/**
 * This function is used to set streaming object and send hit by using comscore api
 * @param  {String} action    The hit action's name (play, pause, stop)
 * @param  {Object} streamVal The streaming config object
 * @return {void}
 */
const send = ({ action, streamVal }: SendObject) => {
  // ready to send
  if (isProd() && contentStreamTag && contentStreamTag[action]) {
    contentStreamTag[action](streamVal);
  }
};

/**
 * This function is used to generate a streaming object and determine whether we save or send this hit
 * @param {WebediaPlayer} webediaPlayer
 * @param  {String} eventAction The hit action's name (play, pause, stop)
 * @param {PlayerCustomData} customData
 * @return {void}
 */
export const trackerHandler = (
  webediaPlayer: WebediaPlayer,
  eventAction: keyof ReducedRequirementsStreamingAnalyticsInstance,
  customData: PlayerCustomData
) => {
  const streamingObj: SendObject = {
    action: eventAction,
    streamVal: {
      c3: '*null',
      c4: '*null',
      c6: '*null',
      ns_st_ce: 0,
      ns_st_ci: '*null',
      ns_st_cl: '*null',
      ns_st_ddt: '*null',
      ns_st_en: '*null',
      ns_st_ep: '*null',
      ns_st_ge: '*null',
      ns_st_ia: 0,
      ns_st_pr: '*null',
      ns_st_pu: '*null',
      ns_st_sn: '*null',
      ns_st_st: '*null',
      ns_st_tdt: '*null',
      ns_st_tep: undefined,
      ns_st_tpr: undefined
    }
  };
  getKeys(configStreaming).forEach(value => {
    streamingObj.streamVal = {
      ...streamingObj.streamVal,
      [value]: configStreaming[value].getValue(webediaPlayer, customData)
    };
  });

  if (libLoaded && typeof contentStreamTag !== 'undefined') {
    for (const tracker of trackerStacks) {
      send(tracker);
    }

    trackerStacks = [];
    send(streamingObj);
  } else {
    trackerStacks.push(streamingObj);
  }
};

export const quartileEventTracking = (
  webediaPlayer: WebediaPlayer,
  { detail: { quartile } }: QuartileEvent,
  customData: PlayerCustomData
) => {
  if (quartile === 0) {
    if (libLoaded) {
      contentStreamTag =
        typeof window.ns_ !== 'undefined'
          ? new window.ns_.ReducedRequirementsStreamingAnalytics({
              publisherId: COMSCORE_CLIENT_ID
            })
          : undefined;
    }
    trackerHandler(webediaPlayer, 'playVideoContentPart', customData);
  } else if (quartile === 4) {
    trackerHandler(webediaPlayer, 'stop', customData);
  }
};

/**
 * This function is used to asynchronously load comscore lib, set flag libLoaded to true and init comscore api
 * @return {void}
 */
export const loadComscoreLib = () => {
  if (typeof contentStreamTag === 'undefined') {
    loadScript(
      ASSETS_HOST + '/bundles/engagement/js/streamsense.min.js',
      () => {
        libLoaded = true;
      }
    );
  }
};
